import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import ShareButton from '../ShareButton';
import { calculateScore, calculateGrade, timeConverter } from '../../utils';
import { FaTrophy, FaClock, FaCheckCircle, FaRedo, FaHome, FaShareAlt } from 'react-icons/fa';
import { motion } from 'framer-motion';

const Stats = ({
  totalQuestions,
  correctAnswers,
  timeTaken,
  replayQuiz,
  resetQuiz,
  isDark = false,
}) => {
  const score = calculateScore(totalQuestions, correctAnswers);
  const { grade, remarks } = calculateGrade(score);
  const { hours, minutes, seconds } = timeConverter(timeTaken);
  const [animatedScore, setAnimatedScore] = useState(0);

  // Check if the user passed or failed
  const isPassed = score >= 60;

  // Animate score on mount
  useEffect(() => {
    const duration = 1500;
    const steps = 60;
    const increment = score / steps;
    let current = 0;

    const timer = setInterval(() => {
      current += increment;
      if (current >= score) {
        setAnimatedScore(score);
        clearInterval(timer);
      } else {
        setAnimatedScore(Math.floor(current));
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [score]);

  return (
    <div className="p-4 sm:p-6 md:p-8 lg:p-10">
      {/* Main Score Card */}
      <motion.div
        className="bg-gradient-to-br from-purple-600 via-violet-600 to-pink-600 rounded-2xl shadow-2xl p-6 sm:p-8 md:p-10 mb-6 text-white relative overflow-hidden"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
      >
        {/* Decorative background elements */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full -ml-12 -mb-12"></div>

        <div className="relative z-10">
          {/* Grade and Remarks */}
          <div className="text-center mb-6">
            <motion.div
              className="inline-block mb-3"
              animate={{
                y: [0, -10, 0],
                rotate: [0, 5, -5, 0]
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                repeatDelay: 1
              }}
            >
              <FaTrophy className={`text-4xl sm:text-5xl ${isPassed ? 'text-yellow-300' : 'text-gray-300'}`} />
            </motion.div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2">{remarks}</h1>
            <h2 className={`text-xl sm:text-2xl md:text-3xl font-semibold ${isPassed ? 'text-yellow-300' : 'text-gray-300'}`}>
              Grade: {grade}
            </h2>
          </div>

          {/* Large Score Display */}
          <div className="text-center mb-6">
            <div className="inline-block bg-white/20 backdrop-blur-md rounded-2xl px-8 sm:px-12 py-6 sm:py-8 border-2 border-white/30">
              <div className="text-5xl sm:text-6xl md:text-7xl font-bold mb-2">
                {animatedScore}%
              </div>
              <div className="text-sm sm:text-base text-white/80">
                Score
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mb-4">
            <div className="flex justify-between text-sm sm:text-base mb-2">
              <span className="font-medium">Progress</span>
              <span className="font-bold">{animatedScore}%</span>
            </div>
            <div className="relative h-4 sm:h-5 bg-white/20 rounded-full overflow-hidden">
              <motion.div
                className={`h-full rounded-full ${isPassed
                    ? 'bg-gradient-to-r from-green-400 to-emerald-500'
                    : 'bg-gradient-to-r from-orange-400 to-red-500'
                  }`}
                initial={{ width: 0 }}
                animate={{ width: `${animatedScore}%` }}
                transition={{ duration: 1.5, ease: 'easeOut' }}
              />
              {score >= 60 && (
                <div className="absolute left-[60%] top-0 bottom-0 w-0.5 bg-white/50"></div>
              )}
            </div>
            <div className="flex justify-between text-xs mt-1 text-white/70">
              <span>0%</span>
              {score >= 60 && <span className="font-medium">60% Pass</span>}
              <span>100%</span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6">
        {[
          { label: 'Total', value: totalQuestions, subLabel: 'Questions', icon: FaCheckCircle, color: 'blue' },
          { label: 'Correct', value: correctAnswers, subLabel: 'Answers', icon: FaCheckCircle, color: 'green' },
          { label: 'Score', value: `${score}%`, subLabel: 'Percentage', icon: FaTrophy, color: 'purple' },
          { label: 'Time', value: `${hours > 0 ? `${Number(hours)}h ` : ''}${minutes > 0 ? `${Number(minutes)}m ` : ''}${Number(seconds)}s`, subLabel: 'Taken', icon: FaClock, color: 'orange' },
        ].map((stat, index) => (
          <motion.div
            key={stat.label}
            className={`rounded-xl p-4 sm:p-5 shadow-lg transition-all duration-300 ${isDark
                ? 'bg-dark-tertiary/50 border border-white/10 hover:border-white/20'
                : 'bg-white border border-gray-100 hover:shadow-xl hover:-translate-y-1'
              }`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 * index }}
          >
            <div className="flex items-center gap-2 mb-2">
              <div className={`p-2 rounded-lg ${isDark
                  ? `bg-${stat.color}-500/20`
                  : `bg-${stat.color}-100`
                }`}>
                <stat.icon className={`text-${stat.color}-${isDark ? '400' : '600'} text-sm sm:text-base`} />
              </div>
              <div className={`text-xs sm:text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {stat.label}
              </div>
            </div>
            <div className={`text-2xl sm:text-3xl font-bold ${stat.color === 'green' ? (isDark ? 'text-green-400' : 'text-green-600') :
                stat.color === 'purple' ? (isDark ? 'text-purple-400' : 'text-purple-600') :
                  stat.color === 'orange' ? (isDark ? 'text-orange-400' : 'text-orange-600') :
                    (isDark ? 'text-blue-400' : 'text-blue-600')
              }`}>
              {stat.value}
            </div>
            <div className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
              {stat.subLabel}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Summary Message */}
      <motion.div
        className={`rounded-xl p-4 sm:p-6 mb-6 ${isDark
            ? 'bg-purple-500/10 border border-purple-500/20'
            : 'bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-100'
          }`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
      >
        <p className={`text-center text-sm sm:text-base mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
          You answered <span className="font-bold text-green-500">{correctAnswers}</span> out of{' '}
          <span className={`font-bold ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>{totalQuestions}</span> questions correctly.
        </p>
        <p className={`text-center text-sm sm:text-base font-semibold ${isPassed ? 'text-green-500' : 'text-orange-500'
          }`}>
          {isPassed
            ? '🎉 Congratulations! You passed the quiz!'
            : '💪 Keep practicing to improve your score!'}
        </p>
      </motion.div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-4 mb-6">
        <motion.button
          className="flex-1 sm:flex-none px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl shadow-lg transition-all duration-300 flex items-center justify-center gap-2 font-semibold text-sm sm:text-base"
          onClick={replayQuiz}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <FaRedo className="text-sm sm:text-base" />
          <span>Try Again</span>
        </motion.button>
        <motion.button
          className={`flex-1 sm:flex-none px-6 sm:px-8 py-3 sm:py-4 rounded-xl shadow-lg transition-all duration-300 flex items-center justify-center gap-2 font-semibold text-sm sm:text-base ${isDark
              ? 'bg-dark-tertiary/50 text-gray-300 border border-white/10 hover:border-purple-500/50'
              : 'bg-white text-gray-700 border-2 border-gray-300 hover:border-purple-500 hover:text-purple-600'
            }`}
          onClick={resetQuiz}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <FaHome className="text-sm sm:text-base" />
          <span>Back to Home</span>
        </motion.button>
      </div>

      {/* Share Section */}
      <div className="flex flex-col items-center gap-3">
        <div className={`px-4 py-2 rounded-full shadow-md hover:shadow-lg transition-all ${isDark ? 'bg-dark-tertiary/50 border border-white/10' : 'bg-white'
          }`}>
          <ShareButton />
        </div>
        <p className={`text-xs sm:text-sm text-center max-w-md ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
          {isPassed
            ? "Share your amazing results with friends and challenge them to beat your score! 🚀"
            : "Share your progress and keep learning! Every attempt makes you better! 💪"}
        </p>
      </div>
    </div>
  );
};

Stats.propTypes = {
  totalQuestions: PropTypes.number.isRequired,
  correctAnswers: PropTypes.number.isRequired,
  timeTaken: PropTypes.number.isRequired,
  replayQuiz: PropTypes.func.isRequired,
  resetQuiz: PropTypes.func.isRequired,
  isDark: PropTypes.bool,
};

export default Stats;